import { getDiagnostic as getState } from "modules/root/root-selectors";
import { createSelector } from "reselect";
import {
  getPainZones,
  getDiagnostics,
  getQuestions,
  getDiagnosticSessions
} from "modules/entity-repository/entity-repository-selectors";
import {
  IgnoredQuestions,
  QuestionKeys
} from "modules/diagnostic/diagnostic-constants";

export const getAnswerGlowing = createSelector(
  getState,
  state => state.answerGlowing
);

export const getMovingNext = createSelector(
  getState,
  state => state.movingNext
);

export const getAnimationState = createSelector(getState, state => ({
  action: state.animationAction,
  state: state.animationState,
  direction: state.animationDirection
}));

export const getCurrentDiagnosticId = createSelector(
  getState,
  state => state.diagnostic
);

export const getCurrentDiagnostic = createSelector(
  getCurrentDiagnosticId,
  getDiagnostics,
  (id, diagnostics) => diagnostics[id]
);

export const getCurrentDiagnosticSession = createSelector(
  getCurrentDiagnosticId,
  getDiagnosticSessions,
  (id, diagnosticSessions) => diagnosticSessions[id]
);

export const getCurrentDiagnosticQuestions = createSelector(
  getCurrentDiagnostic,
  getQuestions,
  (diagnostic, questions) => {
    if (!questions || !diagnostic || !diagnostic.result.questions) {
      return [];
    }
    const diagnosticQuestions = diagnostic.result.questions;
    let questionsResult;
    // Create diagnostic returns object of questions
    // Get diagnostic returns array of IDs
    if (!Array.isArray(diagnosticQuestions)) {
      questionsResult = Object.keys(diagnosticQuestions)
        .map(key => questions[diagnosticQuestions[key].id])
        .filter(Boolean);
    } else {
      questionsResult = diagnostic.result.questions
        .map(id => questions[id])
        .filter(Boolean);
    }

    return questionsResult;
  }
);

export const getPainZoneIds = createSelector(
  getState,
  ({ painZones }) => painZones
);

export const getPainZonesByView = createSelector(
  getPainZoneIds,
  getPainZones,
  ({ front, left, right, back }, painZones) => ({
    front: front.map(id => painZones[id]),
    back: back.map(id => painZones[id]),
    left: left.map(id => painZones[id]),
    right: right.map(id => painZones[id])
  })
);

export const getCurrentDiagnosticSessionQuestions = createSelector(
  getCurrentDiagnosticSession,
  diagnosticSession => {
    if (!diagnosticSession) {
      return [];
    }
    const { questions, answers } = diagnosticSession.result;
    return Object.keys(questions)
      .filter(key => !IgnoredQuestions.includes(key))
      .map(key => {
        const { id, step, question, answer } = questions[key];
        return {
          id,
          step,
          question,
          answer,
          key,
          selectedAnswer: answers[key].value
        };
      })
      .sort((a, b) => a.id - b.id)
      .sort((a, b) => a.step - b.step);
  }
);

export const getChangeAnswerConfirmation = createSelector(
  getState,
  state => state.changeAnswerConfirmed
);

export const getCurrentQuestionId = createSelector(
  getState,
  state => state.currentQuestion
);

export const getCurrentQuestion = createSelector(
  getCurrentQuestionId,
  getQuestions,
  (questionId, questions) => questions[questionId]
);

export const isVASQuestion = createSelector(
  getCurrentQuestion,
  question => question && question.key === QuestionKeys.PAIN_VAS
);

export const isHumanModelQuestion = createSelector(
  getCurrentQuestion,
  question => question && question.key === QuestionKeys.HUMAN_MODEL
);

export const getAllCurrentDiagnosticQuestions = createSelector(
  getCurrentDiagnosticQuestions,
  getCurrentDiagnosticSessionQuestions,
  (currentDiagnosticQuestions, previousQuestions) => {
    const questionsObject = [
      ...previousQuestions,
      ...currentDiagnosticQuestions
    ].reduce((acc, curr) => {
      // group by step
      acc[curr.step] ? acc[curr.step].push(curr) : (acc[curr.step] = [curr]);
      return acc;
    }, {});
    // obj -> arr
    const result = Object.keys(questionsObject)
      .map(key => questionsObject[key])
      .map(questions =>
        // sort within each step
        questions.sort((a, b) => {
          if (a.key < b.key) {
            return -1;
          }
          if (b.key < a.key) {
            return 1;
          }
          return 0;
        })
      )
      // flatten
      .reduce((acc, curr) => [...acc, ...curr], [])
      .reduce((acc, curr) => {
        // remove duplicities
        if (acc.findIndex(item => item.key === curr.key) === -1) {
          acc.push(curr);
        }
        return acc;
      }, []);
    return result;
  }
);

export const getAdjacentQuestions = createSelector(
  getCurrentQuestionId,
  getAllCurrentDiagnosticQuestions,
  (currentQuestionId, allQuestions) => {
    const index = allQuestions.findIndex(item => item.id === currentQuestionId);
    if (index === -1) {
      return {};
    }
    return {
      previous: allQuestions[index - 1],
      current: allQuestions[index],
      next: allQuestions[index + 1]
    };
  }
);



// WEBPACK FOOTER //
// ./src/modules/diagnostic/diagnostic-selectors.js